/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useMemo, ChangeEvent } from 'react';
import MarkdownText from "../MarkdownText";
import SectionElement from './SectionElement';
import Class from '../model/Class';
import { ClassDetails } from '../model/ClassDetails';
import ClassService from '../services/ClassService';

interface ClassPageProps {
  classes: Class[];
  isPrimary: boolean;
  selectedClass: ClassDetails | null;
  selectedArchetype: Class | null;
  onClassChange: (classData: Class | null, archetype: Class | null) => void;
  onLevelChange: (level: number) => void;
  maxLevel: number;
  allowedClassNames?: string[];
  optionsSelectedArrayByOptionNameMap: Map<string, string[]>;
  onSetOption: (optionName: string, optionValue: string[]) => void;
}

const ClassPage: React.FC<ClassPageProps> = ({
  classes,
  isPrimary,
  selectedClass,
  selectedArchetype,
  onClassChange,
  onLevelChange,
  maxLevel,
  allowedClassNames,
  optionsSelectedArrayByOptionNameMap,
  onSetOption
}: ClassPageProps) => {
  const baseClassesOptions = useMemo(() => classes.filter(c => !c.base?.name && (!allowedClassNames || allowedClassNames.includes(c.name))), [classes, allowedClassNames]);
  const archetypesOptions = useMemo(() => classes.filter(c => c.base?.name === selectedClass?.classInfo?.name), [classes, selectedClass]);

  const handleLevelChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onLevelChange(parseInt(event.target.value, 10));
  };

  const handleBaseClassChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const className = event.target.value;
    const newClass = baseClassesOptions.find(cl => cl.name === className) || null;
    onClassChange(newClass, null);
  };

  const handleArchetypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const archetypeName = event.target.value;
    const newArchetype = archetypesOptions.find(cl => cl.name === archetypeName) || null;
    onClassChange(selectedClass?.classInfo || null, newArchetype);
  };

  let prof_armor         = '';
  let prof_weapons       = '';
  let prof_tools         = '';
  let prof_saving_throws = '';
  let prof_skills        = '';
  let equipment          = '';
  
  if (selectedClass?.classInfo) {
    prof_armor         = isPrimary ? selectedClass.classInfo.prof_armor : ClassService.getSecondClassArmorProficiencies(selectedClass.classInfo);
    prof_weapons       = isPrimary ? selectedClass.classInfo.prof_weapons : ClassService.getSecondClassWeaponProficiencies(selectedClass.classInfo);
    prof_tools         = isPrimary ? selectedClass.classInfo.prof_tools : '';
    prof_saving_throws = isPrimary ? selectedClass.classInfo.prof_saving_throws : '';
    prof_skills        = isPrimary ? selectedClass.classInfo.prof_skills : ClassService.getSecondClassSkillProficiencies(selectedClass.classInfo);
    equipment          = isPrimary ? selectedClass.classInfo.equipment : '';
  }
  return (
    <div className='container' style={{overflow: 'hidden'}}>
      <div className="select-container" style={{ width: '100%', backgroundColor: '#f0f0f080', paddingBottom: '8px' }}>
          <b>Select Class: </b>
          <select className="class-selector" value={selectedClass?.classInfo?.name || ''} onChange={handleBaseClassChange}>
            <option value=''>-- Select --</option>
            {baseClassesOptions.map((cls) => (
              <option key={cls.name} value={cls.name}>{cls.name}</option>
            ))}
          </select>
          {selectedClass?.classInfo && (<>
          &nbsp;Level:
          <select className="level-selector" value={selectedClass?.classLevel || 1} onChange={handleLevelChange}>
            {Array.from({ length: maxLevel || 20 }, (_, i) => <option key={i} value={i + 1}>{i + 1}</option>)}
          </select>
          </>)}
        {selectedClass?.classInfo && selectedClass?.classLevel > 2 && (
          <>
            {selectedClass.classInfo?.subtypes_name.endsWith('s') ? selectedClass.classInfo?.subtypes_name.slice(0, -1) : selectedClass.classInfo?.subtypes_name}:&nbsp;
            <select value={selectedArchetype?.name || ''} onChange={handleArchetypeChange}>
              <option value=''>-- Select --</option>
              {archetypesOptions.map((archetype) => (
                <option key={archetype.name} value={archetype.name}>{archetype.name}</option>
              ))}
            </select>
          </>
        )}
        {selectedClass?.classInfo?.sections?.map((section, index) => (
            <SectionElement
              key={index + 'select'}
              section={section}
              index={index}
              optionsSelectedArrayByOptionNameMap={optionsSelectedArrayByOptionNameMap}
              onSetOption={onSetOption}
              showSelect={true}
            />
          ))}
      </div>
      {selectedClass && selectedClass.classInfo && (
        <div className='scrollable-content'>
          <table className="background-table">
            <tbody>
              <tr><th>Hit Dice    </th><td><MarkdownText text={selectedClass.classInfo.hit_dice             || ''}mon={null} /></td></tr>
              <tr><th>Armor Profs </th><td><MarkdownText text={prof_armor                                   || ''}mon={null} /></td></tr>
              <tr><th>Weapon Profs</th><td><MarkdownText text={prof_weapons                                 || ''}mon={null} /></td></tr>
              <tr><th>Tool Profs  </th><td><MarkdownText text={prof_tools                                   || ''}mon={null} /></td></tr>
              <tr><th>Save Profs  </th><td><MarkdownText text={prof_saving_throws                           || ''}mon={null} /></td></tr>
              <tr><th>Skills      </th><td><MarkdownText text={prof_skills                                  || ''}mon={null} /></td></tr>
              <tr><th>Equipment   </th><td><MarkdownText text={equipment                                    || ''}mon={null} /></td></tr>
              <tr><th>Spellcasting</th><td><MarkdownText text={selectedClass.classInfo.spellcasting_ability || ''}mon={null} /></td></tr>
            </tbody>
          </table>
          <MarkdownText text={selectedClass.classInfo?.table || ''} mon={null} />
          {selectedClass.combinedSections?.map((section, index) => (
            <SectionElement
              key={index + 'noSelect'}
              section={section}
              index={index}
              optionsSelectedArrayByOptionNameMap={optionsSelectedArrayByOptionNameMap}
              onSetOption={onSetOption}
              showSelect={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassPage;
